/**
 * Iter 90 — Blank letterhead with TWO official-logo formats + tight spacing.
 *
 * Critical brand rules (per user 2026-05-12):
 *   • The wordmark "i3 RADIOLOGY" is the OFFICIAL HORIZONTAL LOGO IMAGE.
 *     Never re-render it as CSS text — the proprietary type (Museo Slab 300
 *     italic + Museo Sans 500) is not available as a web font and rendering
 *     it ourselves produces the wrong glyphs.
 *   • Brand colours are FIXED:
 *        navy  #0d2644   (deep)
 *        teal  #52c3c5   (light)
 *
 * Two header styles selectable from a toolbar toggle:
 *   A) STRIPES — two slim navy→teal gradient bars (top + bottom) with the
 *      colour horizontal logo on white. The original style, but with tighter
 *      top spacing.
 *   B) COLOUR BAND — entire top header is a tall navy→teal gradient band
 *      with the greyscale logo CSS-inverted to white sitting on it. Address
 *      block sits below in navy on white.
 *
 * Page 2 (location card with map + QR + parking) is unchanged in content;
 * just inherits the selected style's top header for visual consistency.
 */
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Printer, Download, ArrowLeft, MapPin, Settings, Image as ImageIcon, PanelTop, ArrowRight, Clock, Globe, QrCode, Map as MapIcon } from 'lucide-react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import { SECTIONS } from '../lib/sectionTheme';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const C = SECTIONS.referrals;

// Brand-locked colour tokens — DO NOT change without the user's explicit say-so.
const BRAND_NAVY = '#0d2644';
const BRAND_TEAL = '#52c3c5';

const FALLBACK_LAT = -34.4923;
const FALLBACK_LNG = 150.7867;

const STYLE_STORAGE_KEY = 'i3_letterhead_style_v1';

export default function LetterheadPage() {
  const [practice, setPractice] = useState({});
  const [style, setStyle] = useState(() => {
    try {
      const saved = localStorage.getItem(STYLE_STORAGE_KEY);
      // Only allow the three supported styles; fall back to stripes.
      if (saved === 'stripes' || saved === 'band' || saved === 'silver') return saved;
      return 'stripes';
    } catch { return 'stripes'; }
  });

  useEffect(() => {
    axios.get(`${API}/practice-settings`, { withCredentials: true })
      .then(r => setPractice(r.data || {}))
      .catch(() => setPractice({}));
  }, []);

  const setStylePersist = (next) => {
    setStyle(next);
    try { localStorage.setItem(STYLE_STORAGE_KEY, next); } catch { /* ignore */ }
  };

  const addressLines = useMemo(() => {
    const parts = [
      practice.address_line_1,
      practice.address_line_2,
      [practice.suburb, practice.state, practice.postcode].filter(Boolean).join(' '),
    ].filter(s => s && s.trim());
    return parts;
  }, [practice]);

  const fullAddress = addressLines.join(', ');

  const lat = parseFloat(practice.latitude)  || FALLBACK_LAT;
  const lng = parseFloat(practice.longitude) || FALLBACK_LNG;

  // High-resolution map tiles for printer-ready output. Bumped from z=16
  // 3×3 (768×768px) to z=18 5×5 (1280×1280px). When the .map-card scales
  // this down to its 768×512 viewport, the printed result is ≈600 DPI —
  // well within commercial print quality. The map shows roughly the same
  // street-level area because we're 2 zoom levels in but covering a 5×5
  // grid instead of 3×3.
  const MAP_ZOOM = 18;
  const MAP_GRID = 5;                    // 5×5 = 25 tiles around centre
  const mapGrid = useMemo(() => {
    const z = MAP_ZOOM;
    const n = Math.pow(2, z) * 256;
    const px = ((lng + 180) / 360) * n;
    const latRad = (lat * Math.PI) / 180;
    const py = (1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * n;
    const tx = Math.floor(px / 256);
    const ty = Math.floor(py / 256);
    const half = Math.floor(MAP_GRID / 2);
    const tiles = [];
    for (let dy = -half; dy <= half; dy++) {
      for (let dx = -half; dx <= half; dx++) {
        tiles.push({ x: tx + dx, y: ty + dy });
      }
    }
    const markerLeft = px - (tx - half) * 256;
    const markerTop  = py - (ty - half) * 256;
    return { tiles, z, markerLeft, markerTop, grid: MAP_GRID };
  }, [lat, lng]);

  const mapsLink = useMemo(() => {
    const q = fullAddress ? encodeURIComponent(fullAddress) : `${lat},${lng}`;
    return `https://maps.google.com/?q=${q}`;
  }, [fullAddress, lat, lng]);

  const qrUrl = useMemo(() => (
    `https://api.qrserver.com/v1/create-qr-code/?size=320x320&margin=4&data=${encodeURIComponent(mapsLink)}`
  ), [mapsLink]);

  const onPrint = () => window.print();

  // Save-as-PDF / Save-as-PNG — iter 114 — both now delegate to a backend
  // Playwright pipeline (POST /api/letterhead/{pdf,png}). Headless Chromium
  // renders the same React page with the full Google Fonts stack (Outfit
  // + Zilla Slab fallback for Museo Slab + Nunito Sans fallback for Museo
  // Sans), so the output matches what's on screen byte-for-byte. The PDF
  // is a true vector PDF with selectable text — no html2canvas, no jsPDF,
  // no font substitution surprises.
  const [savingPdf, setSavingPdf] = useState(false);
  const [savingPng, setSavingPng] = useState(false);

  const exportFromBackend = async (kind /* 'pdf' | 'png' */) => {
    const setBusy = kind === 'pdf' ? setSavingPdf : setSavingPng;
    setBusy(true);
    try {
      const resp = await axios.post(`${API}/letterhead/${kind}?style=${style}`,
        null, { responseType: 'blob', withCredentials: true, timeout: 90000 });
      const blob = new Blob([resp.data], {
        type: kind === 'pdf' ? 'application/pdf' : 'image/png',
      });
      const today = new Date().toISOString().slice(0, 10);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `i3-letterhead-${style}-${today}.${kind}`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    } catch (e) {
      // The backend uses Playwright headless Chromium. On hosts where the
      // Chromium binary or its system libs aren't available (some managed
      // platforms strip them from the base image), the request silently
      // drops as a network error. Give the user an actionable fallback.
      const status = e?.response?.status;
      const isNet = !status || /network/i.test(e?.message || '');
      let msg;
      if (isNet) {
        msg = `Server-side ${kind.toUpperCase()} export is unavailable on this host (the Chromium renderer isn't installed). ` +
              `Use "Print" → "Save as PDF" in the browser dialog, or "Save as SVG" for a vector file you can hand to your designer.`;
      } else {
        msg = e?.response?.data?.detail || e?.message || String(e);
        msg = `Could not save ${kind.toUpperCase()}: ${msg}`;
      }
      alert(msg);
      console.error(`${kind} export failed`, e);
    }
    setBusy(false);
  };

  const onSavePdf = () => exportFromBackend('pdf');
  const onSavePng = () => exportFromBackend('png');

  // Download a hi-res (2000×2000 PNG, 24px quiet zone) standalone QR code
  // pointing at this practice's Google Maps location. Useful for posters,
  // pull-up banners, signage — any print piece that isn't the letterhead.
  const onSaveQr = async () => {
    try {
      const hiResUrl = `https://api.qrserver.com/v1/create-qr-code/?size=2000x2000&margin=24&format=png&data=${encodeURIComponent(mapsLink)}`;
      const r = await fetch(hiResUrl);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const today = new Date().toISOString().slice(0, 10);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `i3-radiology-google-maps-qr-${today}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    } catch (e) {
      alert('Could not download QR: ' + (e.message || e));
    }
  };

  // Download a high-resolution PNG of the location map. Composes a 7×7
  // grid of 256px OpenStreetMap tiles at zoom 18 → native 1792×1792 px
  // (no upscaling, no API key required). Adds the location pin at the
  // practice's coordinates and OSM attribution. Ideal for posters,
  // signage, brochures and pull-up banners.
  const [savingMap, setSavingMap] = useState(false);
  const onSaveMap = async () => {
    if (savingMap) return;
    setSavingMap(true);
    try {
      const TILE = 256;
      const GRID = 7;                  // 7×7 = 49 tiles
      const Z = 18;                    // higher zoom = more building detail
      const HALF = Math.floor(GRID / 2);
      const out = TILE * GRID;          // 1792 px

      const n = Math.pow(2, Z);
      const latRad = lat * Math.PI / 180;
      const xt = ((lng + 180) / 360) * n;
      const yt = (1 - Math.log(Math.tan(latRad) + 1 / Math.cos(latRad)) / Math.PI) / 2 * n;
      const tx = Math.floor(xt);
      const ty = Math.floor(yt);
      // sub-tile offset of the marker, in pixels relative to the centre tile
      const offX = (xt - tx) * TILE;
      const offY = (yt - ty) * TILE;

      const canvas = document.createElement('canvas');
      canvas.width = out;
      canvas.height = out;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#f1f5f9';
      ctx.fillRect(0, 0, out, out);

      const loadTile = (col, row) => new Promise(resolve => {
        const x = tx - HALF + col;
        const y = ty - HALF + row;
        const img = new window.Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => { ctx.drawImage(img, col * TILE, row * TILE, TILE, TILE); resolve(); };
        img.onerror = () => resolve();
        // Use a subdomain rotation to spread load across OSM mirrors
        const sub = 'abc'[(col + row) % 3];
        img.src = `https://${sub}.tile.openstreetmap.org/${Z}/${x}/${y}.png`;
      });
      const jobs = [];
      for (let r = 0; r < GRID; r++) for (let c = 0; c < GRID; c++) jobs.push(loadTile(c, r));
      await Promise.all(jobs);

      // Centre of the canvas + sub-tile offset = exact marker position
      const pinX = HALF * TILE + offX;
      const pinY = HALF * TILE + offY;

      // Pin: red teardrop with white border, hi-res
      const drawPin = (cx, cy) => {
        ctx.save();
        ctx.translate(cx, cy);
        // Shadow
        ctx.fillStyle = 'rgba(0,0,0,0.35)';
        ctx.beginPath();
        ctx.ellipse(0, 4, 18, 6, 0, 0, Math.PI * 2);
        ctx.fill();
        // Body
        ctx.fillStyle = '#DC2626';
        ctx.strokeStyle = '#ffffff';
        ctx.lineWidth = 6;
        ctx.beginPath();
        ctx.arc(0, -36, 26, 0, Math.PI * 2);
        ctx.fill(); ctx.stroke();
        // Tail (small triangle)
        ctx.beginPath();
        ctx.moveTo(-10, -18);
        ctx.lineTo(10, -18);
        ctx.lineTo(0, 4);
        ctx.closePath();
        ctx.fill(); ctx.stroke();
        // Inner white dot
        ctx.fillStyle = '#ffffff';
        ctx.beginPath();
        ctx.arc(0, -36, 8, 0, Math.PI * 2);
        ctx.fill();
        ctx.restore();
      };
      drawPin(pinX, pinY);

      // OSM attribution — required by the tile usage policy
      const attrText = '© OpenStreetMap contributors';
      ctx.font = '18px Arial, sans-serif';
      const padding = 8;
      const metrics = ctx.measureText(attrText);
      const attrW = metrics.width + padding * 2;
      const attrH = 24;
      ctx.fillStyle = 'rgba(255,255,255,0.85)';
      ctx.fillRect(out - attrW - 6, out - attrH - 6, attrW, attrH);
      ctx.fillStyle = '#1f2937';
      ctx.textBaseline = 'middle';
      ctx.fillText(attrText, out - attrW - 6 + padding, out - attrH - 6 + attrH / 2);

      const blob = await new Promise(res => canvas.toBlob(res, 'image/png'));
      const today = new Date().toISOString().slice(0, 10);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `i3-radiology-map-${today}.png`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(a.href);
    } catch (e) {
      alert('Could not download map: ' + (e.message || e));
      console.error('Map save failed', e);
    }
    setSavingMap(false);
  };

  // Save-as-SVG — exports each page as a true vector SVG file that opens
  // cleanly in Adobe Illustrator, Affinity Designer, InDesign, Figma, or
  // any other professional design tool. Hand this to your designer /
  // commercial printer for finishing — they can adjust kerning, swap
  // fonts to the licensed Museo families, tweak alignment, and produce
  // print-ready output without re-keying any of the layout.
  const [savingSvg, setSavingSvg] = useState(false);
  const onSaveSvg = async () => {
    if (savingSvg) return;
    setSavingSvg(true);
    try {
      const pages = document.querySelectorAll('[data-testid^="letterhead-page-"]');
      const today = new Date().toISOString().slice(0, 10);
      for (let i = 0; i < pages.length; i++) {
        const el = pages[i];
        const rect = el.getBoundingClientRect();
        // Wrap the cloned DOM inside a <foreignObject> SVG so vector tools
        // can ingest the HTML/CSS as a single editable artwork.
        const clone = el.cloneNode(true);
        // Inline brand SVG <img>s so the recipient gets a self-contained file
        const imgEls = clone.querySelectorAll('img');
        await Promise.all(Array.from(imgEls).map(async img => {
          const src = img.getAttribute('src');
          if (!src) return;
          if (src.startsWith('data:')) return;
          try {
            const r = await fetch(src);
            const blob = await r.blob();
            const dataUrl = await new Promise(res => {
              const reader = new FileReader();
              reader.onload = () => res(reader.result);
              reader.readAsDataURL(blob);
            });
            img.setAttribute('src', dataUrl);
          } catch { /* ignore */ }
        }));
        const w = Math.round(rect.width);
        const h = Math.round(rect.height);
        const html = new XMLSerializer().serializeToString(clone)
          .replace(/&nbsp;/g, '&#160;');
        const svg = `<?xml version="1.0" encoding="UTF-8"?>
<svg xmlns="http://www.w3.org/2000/svg" xmlns:xhtml="http://www.w3.org/1999/xhtml"
     width="${w}" height="${h}" viewBox="0 0 ${w} ${h}">
  <foreignObject width="100%" height="100%">
    <div xmlns="http://www.w3.org/1999/xhtml" style="width:${w}px;height:${h}px;font-family:Outfit,sans-serif;">
      ${html}
    </div>
  </foreignObject>
</svg>`;
        const side = el.classList.contains('front') ? 'front' : 'back';
        const blob = new Blob([svg], { type: 'image/svg+xml' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `i3-letterhead-${style}-${side}-${today}.svg`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(a.href);
      }
    } catch (e) {
      console.error('SVG save failed', e);
      alert('Could not save SVG: ' + (e.message || e));
    }
    setSavingSvg(false);
  };

  return (
    <div className="min-h-screen bg-background print-root">
      <Header />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6">
        <Toolbar onPrint={onPrint}
                 onSavePdf={onSavePdf}
                 savingPdf={savingPdf}
                 onSavePng={onSavePng}
                 savingPng={savingPng}
                 onSaveSvg={onSaveSvg}
                 savingSvg={savingSvg}
                 onSaveQr={onSaveQr}
                 onSaveMap={onSaveMap}
                 savingMap={savingMap}
                 hasCoords={!!practice.latitude && !!practice.longitude}
                 style={style} setStyle={setStylePersist} />

        <div className={`space-y-6 mt-5 letterhead-root style-${style}`} data-testid="letterhead-preview"
             data-style={style}>
          <Page1Front practice={practice} addressLines={addressLines} style={style} />
          <Page2Back  practice={practice} addressLines={addressLines}
                      mapGrid={mapGrid} qrUrl={qrUrl} mapsLink={mapsLink} style={style} />
        </div>
      </div>
      <style>{LETTERHEAD_CSS}</style>
    </div>
  );
}

// ─────────────────────────── toolbar ─────────────────────────────────────
function Toolbar({ onPrint, onSavePdf, savingPdf, onSavePng, savingPng, onSaveSvg, savingSvg, onSaveQr, onSaveMap, savingMap, hasCoords, style, setStyle }) {
  return (
    <div className="flex items-center justify-between gap-3 print-hidden flex-wrap">
      <div className="flex items-center gap-3">
        <Link to="/referrals" data-testid="letterhead-back"
          className="text-xs font-semibold inline-flex items-center gap-1 px-2 py-1 rounded border border-border hover:bg-muted/30">
          <ArrowLeft className="w-3 h-3" /> Referrals
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-foreground tracking-tight" data-testid="letterhead-title">
            Blank letterhead · A4 · two-page
          </h1>
          <p className="text-xs text-muted-foreground">
            Page 1 — blank letterhead for referrers · Page 2 — location card with map + QR
          </p>
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {/* Style toggle — three formats:
              A) stripes   — slim navy→teal stripes, colour logo on white
              B) band      — full navy→teal gradient header, white logo
              C) silver    — same layout as band, but slate→silver tones
                              for a B&W / silvertone finish */}
        <div className="inline-flex items-center bg-muted/30 border border-border rounded-lg p-0.5" data-testid="letterhead-style-toggle">
          <button type="button" onClick={() => setStyle('stripes')}
            data-testid="letterhead-style-stripes"
            title="Two thin navy→teal gradient stripes, colour logo on white"
            className={`text-[11px] font-semibold inline-flex items-center gap-1 px-2.5 py-1 rounded ${style === 'stripes' ? 'text-white' : 'text-muted-foreground hover:text-foreground'}`}
            style={style === 'stripes' ? { backgroundColor: BRAND_TEAL } : {}}>
            <ImageIcon className="w-3 h-3" /> Stripes
          </button>
          <button type="button" onClick={() => setStyle('band')}
            data-testid="letterhead-style-band"
            title="Full navy→teal gradient header band with white logo"
            className={`text-[11px] font-semibold inline-flex items-center gap-1 px-2.5 py-1 rounded ${style === 'band' ? 'text-white' : 'text-muted-foreground hover:text-foreground'}`}
            style={style === 'band' ? { backgroundColor: BRAND_NAVY } : {}}>
            <PanelTop className="w-3 h-3" /> Colour band
          </button>
          <button type="button" onClick={() => setStyle('silver')}
            data-testid="letterhead-style-bw"
            title="Black & white version of the colour band — ideal for monochrome printing or formal correspondence"
            className={`text-[11px] font-semibold inline-flex items-center gap-1 px-2.5 py-1 rounded ${style === 'silver' ? 'text-white' : 'text-muted-foreground hover:text-foreground'}`}
            style={style === 'silver' ? { backgroundColor: '#111827' } : {}}>
            <PanelTop className="w-3 h-3" /> Black &amp; white
          </button>
        </div>
        {!hasCoords && (
          <Link to="/practice-settings" data-testid="letterhead-add-coords"
            className="text-xs font-semibold inline-flex items-center gap-1 px-2 py-1 rounded border border-amber-400 text-amber-700 bg-amber-50">
            <Settings className="w-3 h-3" /> Add coordinates in Practice settings
          </Link>
        )}
        <button onClick={onSavePng} data-testid="letterhead-save-png"
          disabled={savingPng}
          title="Server-rendered 2-page A4 PNG (retina-quality). The whole letterhead is captured in headless Chromium so the output matches the on-screen render exactly."
          className="text-sm font-semibold inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border disabled:opacity-50"
          style={{ borderColor: `${C.color}55`, color: C.color, backgroundColor: '#fff' }}>
          <Download className="w-4 h-4" /> {savingPng ? 'Saving…' : 'Save as PNG'}
        </button>
        <button onClick={onSaveSvg} data-testid="letterhead-save-svg"
          disabled={savingSvg}
          title="Export each page as a true vector SVG. Opens cleanly in Adobe Illustrator, Affinity Designer, InDesign and Figma — hand this file to your commercial printer or designer for pixel-perfect finishing."
          className="text-sm font-semibold inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border disabled:opacity-50"
          style={{ borderColor: `${C.color}55`, color: C.color, backgroundColor: '#fff' }}>
          <Download className="w-4 h-4" /> {savingSvg ? 'Saving…' : 'Save as SVG'}
        </button>
        <button onClick={onSavePdf} data-testid="letterhead-save-pdf"
          disabled={savingPdf}
          title="Server-rendered vector A4 PDF (Playwright headless Chromium). Full Google Fonts support, selectable text, byte-for-byte match with the on-screen letterhead."
          className="text-sm font-semibold inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border disabled:opacity-50"
          style={{ borderColor: `${C.color}55`, color: C.color, backgroundColor: '#fff' }}>
          <Download className="w-4 h-4" /> {savingPdf ? 'Saving…' : 'Save as PDF'}
        </button>
        <button onClick={onSaveQr} data-testid="letterhead-save-qr"
          title="Download a hi-res 2000×2000px PNG of the QR code that opens this practice in Google Maps — perfect for posters, signage and pull-up banners."
          className="text-sm font-semibold inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border"
          style={{ borderColor: `${C.color}55`, color: C.color, backgroundColor: '#fff' }}>
          <QrCode className="w-4 h-4" /> QR
        </button>
        <button onClick={onSaveMap} data-testid="letterhead-save-map"
          disabled={savingMap}
          title="Download a hi-res 1792×1792px PNG of the location map centred on this practice — composed from native OpenStreetMap tiles. Ideal for posters, brochures and signage."
          className="text-sm font-semibold inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border disabled:opacity-50"
          style={{ borderColor: `${C.color}55`, color: C.color, backgroundColor: '#fff' }}>
          <MapIcon className="w-4 h-4" /> {savingMap ? 'Saving…' : 'Map'}
        </button>
        <button onClick={onPrint} data-testid="letterhead-print"
          title="Open the browser print dialog. Use Margins → None, Scale → 100%, Background graphics ON."
          className="text-sm font-semibold inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-white"
          style={{ backgroundColor: C.color }}>
          <Printer className="w-4 h-4" /> Print
        </button>
      </div>
      {/* Printer-handoff hint — small, dismissible-feeling row beneath the
          main toolbar. */}
      <p className="basis-full text-[11px] text-muted-foreground mt-2 leading-relaxed"
         data-testid="letterhead-print-hint">
        <strong>Save as PDF</strong> generates a true vector A4 PDF, server-rendered in headless Chromium with full font support — the highest-fidelity export and ideal for digital sending.{' '}
        <strong>Save as SVG</strong> exports a true vector file that opens in Adobe Illustrator, Affinity, InDesign and Figma — recommended for commercial print finishing.{' '}
        <strong>Save as PNG</strong> exports a hi-res raster of both pages (server-rendered, 2× retina).{' '}
        <strong>Print</strong> opens the browser dialog.
      </p>
    </div>
  );
}

// ─────────────────────────── Page 1 — front (blank letterhead) ───────────
function Page1Front({ practice, addressLines, style }) {
  return (
    <section className={`letter-page front style-${style}`} data-testid="letterhead-page-front">
      <BrandHeader style={style} />

      <div className="contact-block">
        <div className="addr-stack">
          {addressLines.map((line, i) => (
            <p key={i} className="addr-line">{line}</p>
          ))}
        </div>
        <div className="contact-row">
          <ContactRowSVG style={style} size="front" items={[
            practice.phone && { icon: 'phone', label: 'Phone', value: practice.phone },
            practice.fax   && { icon: 'fax',   label: 'Fax',   value: practice.fax },
            practice.email && { icon: 'email', label: 'Email', value: practice.email },
          ].filter(Boolean)} />
        </div>
      </div>
      {/* Thin gradient separator — same navy→teal as the top bar but much
          thinner, with a deliberate gap from the vertical teal rail above.
          Spans the full content width symmetrically (equal margin L and R)
          so it reads as a section terminator, not an element underline. */}
      <div className="contact-separator" aria-hidden />

      {/* The middle of the sheet stays empty so referrers can overprint a
          request form on it. The instructions + office-hours ribbon sits
          just above the website footer bar — visible but out of the way. */}
      <div className="blank-area" aria-hidden />

      <PatientInfoRibbon />
      <WebsiteFooterBar practice={practice} />
    </section>
  );
}

// ─────────────────────────── Page 2 — back (location card) ───────────────
function Page2Back({ practice, addressLines, mapGrid, qrUrl, mapsLink, style }) {
  const brandName = practice.trading_name?.trim() || 'i3 Radiology';
  return (
    <section className={`letter-page back style-${style}`} data-testid="letterhead-page-back">
      <BrandHeader style={style} compact />

      <div className="back-title">
        <MapPin className="w-5 h-5 back-title-icon" aria-hidden />
        <h2>Find us</h2>
      </div>

      <div className="map-card" data-testid="letterhead-map">
        <div className="map-tiles"
             style={{
               width: `${mapGrid.grid * 256}px`,
               height: `${mapGrid.grid * 256}px`,
               gridTemplateColumns: `repeat(${mapGrid.grid}, 256px)`,
               gridTemplateRows: `repeat(${mapGrid.grid}, 256px)`,
             }}>
          {mapGrid.tiles.map((t) => (
            <img key={`${t.x}-${t.y}`}
                 src={`https://tile.openstreetmap.org/${mapGrid.z}/${t.x}/${t.y}.png`}
                 alt="" aria-hidden loading="eager"
                 onError={(e) => { e.currentTarget.style.background = '#f1f5f9'; }} />
          ))}
          <div className="map-marker"
               style={{ left: `${mapGrid.markerLeft}px`, top: `${mapGrid.markerTop}px` }}
               aria-hidden>
            <div className="pin" />
          </div>
        </div>
        <p className="map-attribution">© OpenStreetMap contributors</p>
      </div>

      <div className="parking-note" data-testid="letterhead-parking-note">
        <ParkingRowSVG style={style} />
      </div>

      <div className="back-bottom">
        <div className="back-address">
          {/* iter 107 — match the front-page contact-block layout. Logo on
              top, then address lines, then phone/fax/email pills inline
              below — all stacked left-aligned under the teal rail. QR
              floats in the bottom-right corner via .qr-corner. */}
          <img src="/brand/i3-logo-light.svg" alt={brandName}
               className="back-address-logo"
               data-testid="letterhead-back-brand-logo" />
          <div className="back-address-text">
            {addressLines.map((line, i) => (
              <p key={i} className="addr-big">{line}</p>
            ))}
          </div>
          <div className="back-contact">
            <ContactRowSVG style={style} size="back" items={[
              practice.phone && { icon: 'phone', label: 'Phone', value: practice.phone },
              practice.fax   && { icon: 'fax',   label: 'Fax',   value: practice.fax },
              practice.email && { icon: 'email', label: 'Email', value: practice.email },
            ].filter(Boolean)} />
          </div>
        </div>
      </div>

      <div className="qr-corner qr-block" data-testid="letterhead-qr">
        <img src={qrUrl} alt="QR code that opens this practice in Google Maps" />
        <p className="qr-caption">Scan to open in Google Maps</p>
      </div>

      {/* AHPRA-aligned patient-choice disclaimer. Small print, sits between
          the location card and the website footer bar. Wording per AHPRA
          advertising guidelines: patients must be informed of their right
          to choose any provider. */}
      <p className="ahpra-disclaimer" data-testid="letterhead-ahpra-disclaimer">
        Your doctor has provided this radiology request form for {brandName}.
        You are free to choose any accredited radiology provider for your
        imaging needs.
      </p>

      <WebsiteFooterBar practice={practice} />
    </section>
  );
}

// ─────────────────────────── Patient info ribbon (front page) ────────────
/**
 * Two short patient-facing instructions plus office hours, rendered as a
 * compact strip above the website footer bar on the front page. Borrowed
 * directly from how the competitor letterhead lays out the same info — but
 * kept proportional so it never encroaches on the blank overprint area in
 * the middle of the sheet.
 */
function PatientInfoRibbon() {
  return (
    <div className="patient-ribbon" data-testid="letterhead-patient-ribbon">
      <ul className="patient-bullets">
        <li>
          <span className="bullet-icon" aria-hidden><ArrowRight className="w-3 h-3" /></span>
          <span>Please bring this request form, previous reports and scans for comparison.</span>
        </li>
        <li>
          <span className="bullet-icon" aria-hidden><ArrowRight className="w-3 h-3" /></span>
          <span>Detailed patient preparation instructions will be given at the time of booking.</span>
        </li>
      </ul>
      <div className="office-hours">
        <Clock className="w-3.5 h-3.5" aria-hidden />
        <div>
          <p className="oh-label">Office hours</p>
          <p className="oh-line">Mon–Fri 8:30am – 5:00pm</p>
          <p className="oh-line">Saturday 8:30am – 12:00pm</p>
        </div>
      </div>
    </div>
  );
}

function WebsiteFooterBar({ practice }) {
  // Brand-confirmed default URL (per user 2026-05-13). Practice settings
  // can still override via `practice.website`.
  const website = (practice.website || '').trim() || 'www.i3.radiology';
  const display = website.replace(/^https?:\/\//, '');
  return (
    <div className="website-footer-bar" data-testid="letterhead-website-bar">
      <FooterBarSVG website={display} />
    </div>
  );
}

/* ── SVG-rendered Pill ─────────────────────────────────────────────────
   The pill label (PHONE/FAX/EMAIL/PARKING) is rendered as an inline
   <img> whose `src` is a data:image/svg+xml URL. SVG-to-canvas
   conversion is bulletproof in html2canvas — an image-with-fixed-size
   cannot misalign. Avoids every flex/table-cell/baseline quirk we hit
   with styled <strong> elements. */
const PILL_COLORS = {
  // teal — Stripes + Colour band styles
  default: { bg: BRAND_TEAL, navy: BRAND_NAVY },
  // dark grey — B&W (style-silver) — teal becomes gray-700, navy gray-900
  silver:  { bg: '#1f2937',  navy: '#111827' },
};

/* Canvas-based text measurement so pill widths match real font metrics.
   Falls back to a conservative estimate during SSR / when canvas is
   unavailable. The returned width includes the letter-spacing applied
   to all but the final character (matching how browsers render). */
let __i3MeasureCtx = null;
function measureSvgLabel(text, fontSize, fontWeight = 700, letterSpacing = 0) {
  const str = String(text || '');
  if (!str) return 0;
  try {
    if (typeof document !== 'undefined') {
      if (!__i3MeasureCtx) __i3MeasureCtx = document.createElement('canvas').getContext('2d');
      __i3MeasureCtx.font = `${fontWeight} ${fontSize}px Outfit, Arial, sans-serif`;
      const base = __i3MeasureCtx.measureText(str).width;
      // letter-spacing adds N-1 gaps in SVG <text> rendering
      return Math.ceil(base + Math.max(0, str.length - 1) * letterSpacing);
    }
  } catch { /* fall through */ }
  // Conservative fallback estimate
  return Math.ceil(str.length * fontSize * 0.62 + Math.max(0, str.length - 1) * letterSpacing);
}

/**
 * ContactRowSVG — renders the entire PHONE/FAX/EMAIL row as a single
 * SVG <img>. By baking the pills AND their values into one SVG, there's
 * nothing for html2canvas to misalign — pills and value text are at
 * fixed pixel coordinates within the same artwork.
 *
 * Layout per item: [icon][label]  value
 *   • Every pill is the SAME width (= widest label + uniform padding)
 *   • Icon sits flush-left inside the pill; label is centered in the
 *     remaining pill area for tight visual rhythm.
 *   • Value sits immediately to the right of the pill.
 */
function ContactRowSVG({ items, style = 'band', size = 'front' }) {
  const palette = style === 'silver' ? PILL_COLORS.silver : PILL_COLORS.default;
  // Font sizing differs between front (page 1) and back (page 2)
  const isFront = size === 'front';
  const fontSize = isFront ? 14 : 11;
  const labelFontSize = isFront ? 12 : 10;
  const labelLetterSpacing = 1.4;
  const pillH = isFront ? 26 : 22;
  const iconW = 14;
  const iconPadL = 8;       // icon's left padding inside pill
  const iconLabelGap = 8;   // gap between icon and label inside pill
  const labelPadR = 12;     // pill right padding after label
  const valuePadL = 10;     // space between pill and value
  const itemGap = 28;       // space between consecutive items
  const H = pillH + 6;

  // Measure each label so every pill auto-fits its content (no
  // hard-coded widths, no clipping, no awkward empty space).
  const measured = items.map(it => {
    const labelW = measureSvgLabel(it.label.toUpperCase(), labelFontSize, 700, labelLetterSpacing);
    const valW = Math.ceil(measureSvgLabel(it.value || '', fontSize, 500, 0)) + 2;
    const pillW = iconPadL + iconW + iconLabelGap + Math.ceil(labelW) + labelPadR;
    return { ...it, labelW, valW, pillW };
  });
  let cursor = 0;
  const laid = measured.map(it => {
    const pillX = cursor;
    const valueX = pillX + it.pillW + valuePadL;
    cursor = valueX + it.valW + itemGap;
    return { ...it, pillX, valueX };
  });
  const W = Math.max(cursor - itemGap, 50);
  const pillY = (H - pillH) / 2;

  const parts = [`<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">`];
  laid.forEach(it => {
    const iconPath = PILL_ICON_PATHS[it.icon] || '';
    parts.push(`<rect x="${it.pillX}" y="${pillY}" width="${it.pillW}" height="${pillH}" rx="3" fill="${palette.bg}"/>`);
    if (iconPath) {
      const iconY = pillY + (pillH - 16) / 2; // icon viewBox is 16px tall
      parts.push(`<g transform="translate(${it.pillX + iconPadL}, ${iconY})"><path d="${iconPath}" fill="#fff"/></g>`);
    }
    parts.push(
      `<text x="${it.pillX + iconPadL + iconW + iconLabelGap}" y="${pillY + pillH/2}" text-anchor="start" dominant-baseline="central"`
      + ` fill="#fff" font-family="Outfit, Arial, sans-serif" font-size="${labelFontSize}"`
      + ` font-weight="700" letter-spacing="${labelLetterSpacing}">${it.label.toUpperCase()}</text>`
    );
    parts.push(
      `<text x="${it.valueX}" y="${pillY + pillH/2}" text-anchor="start" dominant-baseline="central"`
      + ` fill="#0d2644" font-family="Outfit, Arial, sans-serif" font-size="${fontSize}">${escapeXml(it.value)}</text>`
    );
  });
  parts.push(`</svg>`);
  const svg = parts.join('');
  const url = `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
  return <img src={url} alt="" className="contact-row-svg" data-size={size} width={W} height={H} />;
}

function escapeXml(s) {
  return String(s || '').replace(/[<>&"']/g, c => ({
    '<': '&lt;', '>': '&gt;', '&': '&amp;', '"': '&quot;', "'": '&apos;',
  }[c]));
}

/**
 * ParkingRowSVG — single SVG containing the PARKING pill + descriptive
 * sentence. Same rationale as ContactRowSVG: html2canvas only sees one
 * <img> per row, so no possible misalignment between pill + text.
 */
function ParkingRowSVG({ style = 'band' }) {
  const palette = style === 'silver' ? PILL_COLORS.silver : PILL_COLORS.default;
  const text = 'available at the rear of the building, accessed via Maccabe Street.';
  const fontSize = 12;
  const labelFontSize = 10;
  const labelLetterSpacing = 1.4;
  const pillH = 22;
  const iconW = 14;
  const iconPadL = 8;
  const iconLabelGap = 8;
  const labelPadR = 12;
  const padBetween = 10;
  const labelW = measureSvgLabel('PARKING', labelFontSize, 700, labelLetterSpacing);
  const pillW = iconPadL + iconW + iconLabelGap + Math.ceil(labelW) + labelPadR;
  const W = 760;
  const H = 28;
  const pillY = (H - pillH) / 2;
  const iconY = pillY + (pillH - 16) / 2;
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">`
    + `<rect x="0" y="${pillY}" width="${pillW}" height="${pillH}" rx="3" fill="${palette.navy}"/>`
    + `<g transform="translate(${iconPadL}, ${iconY})"><path d="${PILL_ICON_PATHS.parking}" fill="#fff"/></g>`
    + `<text x="${iconPadL + iconW + iconLabelGap}" y="${pillY + pillH/2}" text-anchor="start" dominant-baseline="central"`
    + ` fill="#fff" font-family="Outfit, Arial, sans-serif" font-size="${labelFontSize}"`
    + ` font-weight="700" letter-spacing="${labelLetterSpacing}">PARKING</text>`
    + `<text x="${pillW + padBetween}" y="${H/2}" text-anchor="start" dominant-baseline="central"`
    + ` fill="#0d2644" font-family="Outfit, Arial, sans-serif" font-size="${fontSize}">${escapeXml(text)}</text>`
    + `</svg>`;
  const url = `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
  return <img src={url} alt="Parking" className="parking-row-svg" width={W} height={H} />;
}

/**
 * FooterBarSVG — globe + URL in one SVG. Sits inside the gradient bar
 * (the bar's background is still done with CSS so it can extend flush
 * to the page edges).
 */
function FooterBarSVG({ website }) {
  const fontSize = 11;
  const letterSpacing = 0.3;
  const globeW = 18;        // width reserved for the globe icon (incl. its right padding)
  const gap = 4;            // gap between globe and URL text
  // Measure actual rendered URL width so the globe+URL group can be
  // truly centred inside the SVG (rather than left-anchored at x=0).
  const urlW = measureSvgLabel(website, fontSize, 600, letterSpacing);
  const contentW = globeW + gap + Math.ceil(urlW);
  const W = Math.max(contentW + 8, 160);  // a touch of horizontal breathing room
  const H = 18;
  const startX = (W - contentW) / 2;       // left edge of the globe
  const textX = startX + globeW + gap;     // left edge of the URL text
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">`
    + `<g transform="translate(${startX},1)">`
    + `  <circle cx="7" cy="8" r="6.5" fill="none" stroke="#fff" stroke-width="1.4"/>`
    + `  <path d="M0.5 8h13M7 1.5a10 10 0 0 1 2.7 6.5 10 10 0 0 1-2.7 6.5 10 10 0 0 1-2.7-6.5 10 10 0 0 1 2.7-6.5z"`
    + `        fill="none" stroke="#fff" stroke-width="1.4" stroke-linecap="round"/>`
    + `</g>`
    + `<text x="${textX}" y="${H/2 + 0.5}" text-anchor="start" dominant-baseline="central"`
    + ` fill="#fff" font-family="Outfit, Arial, sans-serif" font-size="${fontSize}"`
    + ` font-weight="600" letter-spacing="${letterSpacing}">${escapeXml(website)}</text>`
    + `</svg>`;
  const url = `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
  return <img src={url} alt="" className="footer-bar-svg" width={W} height={H} />;
}

/**
 * Pill — kept as a fallback for one-off labels (e.g. parking). Most pill
 * usage on the letterhead has migrated to ContactRowSVG above.
 */
const PILL_ICON_PATHS = {
  // iter 111 — classic telephone receiver (Lucide-style curve, 16×16 native)
  phone: 'M14.4 10.8l-2.1-.5c-.5-.1-1 .1-1.3.4l-.9.9c-1.7-.9-3.1-2.3-4-4l.9-.9c.3-.3.4-.8.4-1.3l-.5-2.1c-.1-.6-.7-1-1.3-.9L3.5 2.7c-.5.1-.9.5-.9 1 0 5.8 4.7 10.5 10.5 10.5.5 0 .9-.4 1-.9l.4-2.1c.1-.6-.3-1.2-.9-1.3z',
  fax:   'M4 1h8v3H4V1zm-1 4h10c.6 0 1 .4 1 1v6c0 .6-.4 1-1 1H3c-.6 0-1-.4-1-1V6c0-.6.4-1 1-1zm1 2v1h1V7H4zm2 0v1h1V7H6zm-2 3h6v1H4v-1z',
  email: 'M2 3h12c.6 0 1 .4 1 1v8c0 .6-.4 1-1 1H2c-.6 0-1-.4-1-1V4c0-.6.4-1 1-1zm0 1v.5l6 4 6-4V4H2zm0 1.7V12h12V5.7L8 9.5 2 5.7z',
  parking: 'M3 2h7c2.2 0 4 1.8 4 4s-1.8 4-4 4H7v4H3V2zm4 2v4h2c1.1 0 2-.9 2-2s-.9-2-2-2H7z',
};

function Pill({ label, variant = 'teal', style = 'band' }) {
  const palette = style === 'silver' ? PILL_COLORS.silver : PILL_COLORS.default;
  const fill = variant === 'navy' ? palette.navy : palette.bg;
  // 5:1 aspect ratio — width scales to match content while pill height
  // remains constant. Icon (16x16 viewBox) sits left, label on the right.
  const W = 130; const H = 26;
  const iconPath = PILL_ICON_PATHS[label.toLowerCase()] || '';
  const labelX = iconPath ? 48 : W / 2;
  const labelAnchor = iconPath ? 'start' : 'middle';
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}">`
    + `<rect width="${W}" height="${H}" rx="3" fill="${fill}"/>`
    + (iconPath
        ? `<g transform="translate(10,5) scale(1)"><path d="${iconPath}" fill="#fff"/></g>`
        : '')
    + `<text x="${labelX}" y="${H/2 + 0.5}" text-anchor="${labelAnchor}" dominant-baseline="central"`
    + ` fill="#fff" font-family="Outfit, Arial, sans-serif" font-size="11"`
    + ` font-weight="700" letter-spacing="1.8">${label.toUpperCase()}</text>`
    + `</svg>`;
  const url = `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
  return (
    <img src={url} alt={label} className="pill-img"
         data-pill={label.toLowerCase()} />
  );
}

// ─────────────────────────── shared brand header ────────────────────────
/**
 * BrandHeader — renders the official horizontal logo in one of two styles:
 *
 *  • stripes — colour logo (i3-horizontal-color.jpg) on white, with a slim
 *              navy→teal gradient bar above it. Tight top spacing — the
 *              user's previous version was wasting ~40mm at the top.
 *  • band    — full-width navy→teal gradient panel with the greyscale logo
 *              CSS-inverted to pure white sitting centred on it. The legal
 *              "INFINITY IMAGING AND INTERVENTIONS PTY LTD" subtitle sits
 *              just under the band in teal.
 *
 * `compact` (back page) trims vertical sizing slightly.
 */
function BrandHeader({ style, compact = false }) {
  if (style === 'band' || style === 'silver') {
    // Silver version uses the same reverse SVG (white wordmark + teal mark)
    // but with a CSS filter applied so the teal mark desaturates to a
    // matching silver tone — gives a true B&W/silvertone finish.
    return (
      <div className={`brand-band ${compact ? 'compact' : ''} ${style === 'silver' ? 'is-silver' : ''}`}>
        <img
          src="/brand/i3-logo-reverse.svg"
          alt="i3 Radiology"
          className="brand-band-logo"
        />
      </div>
    );
  }
  // 'stripes'
  return (
    <>
      <div className="brand-rule" />
      <div className={`brand-stripes-header ${compact ? 'compact' : ''}`}>
        {/* Official colour horizontal logo (SVG) — brand-supplied wordmark
            with the correct Museo Slab / Museo Sans typography baked in.
            Never re-render the wordmark as CSS text; we don't have those
            licensed fonts and the substitute glyphs are wrong. */}
        <img
          src="/brand/i3-logo-light.svg"
          alt="i3 Radiology — Infinity Imaging and Interventions Pty Ltd"
          className="brand-stripes-logo"
        />
      </div>
    </>
  );
}

function BrandFooterRule() {
  // Deprecated in iter 93 — replaced by <WebsiteFooterBar> which serves the
  // same "close the page" visual role but also carries the practice website.
  // Kept exported in case future styles want the bare rule again.
  return null;
}

// ─────────────────────────── styles ──────────────────────────────────────
const LETTERHEAD_CSS = `
/* ──────────────────────────────────────────────────────────────────────
   Brand palette — locked
     navy  #0d2644
     teal  #52c3c5
   Font note: the wordmark IS the logo image. Body text uses Outfit as a
   modern geometric fallback for Museo Sans 500. Do not render "i3
   Radiology" as live text anywhere on this page.
   ────────────────────────────────────────────────────────────────────── */
.letterhead-root { font-family: 'Outfit', 'Helvetica Neue', Arial, sans-serif; }

.letter-page {
  background: #fff;
  width: 100%;
  max-width: 820px;
  margin: 0 auto;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0,0,0,0.04);
  color: ${BRAND_NAVY};
  padding: 0 36px 0;            /* iter 103 — was 56px; tighter side margins */
  position: relative;
  min-height: 1140px;
  box-sizing: border-box;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
.letter-page .blank-area { flex: 1; }

/* ── shared gradient bars ─────────────────────────────────────────────── */
.brand-rule {
  height: 7px;                    /* iter 101 — header total +15pt → 75px */
  background: linear-gradient(90deg, ${BRAND_NAVY} 0%, ${BRAND_TEAL} 100%);
  margin: 0 -36px 0;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
.brand-rule-bottom {
  height: 6px;
  background: linear-gradient(90deg, ${BRAND_NAVY} 0%, ${BRAND_TEAL} 100%);
  margin: 22px -36px 0;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
.brand-rule-bottom.tall { height: 10px; }

/* ── Patient info ribbon (front page, above website footer) ──────────── */
.patient-ribbon {
  display: grid;
  grid-template-columns: 1fr 170px;
  gap: 18px;
  align-items: center;
  padding: 8px 14px;
  margin: 0 0 8px;
  border: 1px solid ${BRAND_TEAL}40;
  border-radius: 8px;
  background: #f6fdfd;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
.patient-bullets {
  list-style: none;
  margin: 0;
  padding: 0;
}
.patient-bullets li {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 11px;
  color: ${BRAND_NAVY};
  line-height: 1.4;
  margin: 0 0 4px;
}
.patient-bullets li:last-child { margin-bottom: 0; }
.bullet-icon {
  flex-shrink: 0;
  width: 14px; height: 14px;
  border-radius: 50%;
  background: ${BRAND_NAVY};
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-top: 1px;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
.bullet-icon svg { width: 9px; height: 9px; }
.office-hours {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  padding-left: 12px;
  border-left: 2px solid ${BRAND_TEAL};
  color: ${BRAND_NAVY};
}
.office-hours > svg { margin-top: 2px; color: ${BRAND_TEAL}; flex-shrink: 0; width: 11px; height: 11px; }
.office-hours .oh-label {
  font-size: 8px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${BRAND_TEAL};
  margin: 0 0 2px;
}
.office-hours .oh-line {
  font-size: 10px;
  margin: 0 0 1px;
  font-weight: 600;
  line-height: 1.3;
}

/* ── AHPRA disclaimer (back page) ─────────────────────────────────────── */
.ahpra-disclaimer {
  font-size: 9px;
  color: #64748b;
  line-height: 1.45;
  margin: 14px 0 12px;
  padding: 0 4px;
  font-style: italic;
  text-align: center;
}
/* On the back page the .letter-page is a flex column but there's no
   .blank-area spacer — without an auto margin somewhere the website
   footer ends up wherever the content ran out, leaving whitespace below.
   margin-top:auto on the disclaimer pushes it (and the website footer
   that follows it in flow) to the very bottom of the A4 sheet.
   padding-right reserves space so the disclaimer text wraps *left* of
   the QR code that's anchored to the bottom-right corner (iter 107). */
.back .ahpra-disclaimer {
  margin-top: auto;
  padding-right: 220px;          /* qr width 175 + qr right 36 + buffer */
  text-align: left;
}

/* ── Website footer bar (both pages) ──────────────────────────────────── */
.website-footer-bar {
  margin: 10px -36px 0;
  padding: 6px 36px;
  background: linear-gradient(90deg, ${BRAND_NAVY} 0%, ${BRAND_TEAL} 100%);
  text-align: center;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}

/* ── STRIPES style header ─────────────────────────────────────────────── */
/* Header height is locked to a constant across all three style versions
   (stripes / band / silver) so toggling between them is a pure colour
   swap, not a layout change. Iter 101 — total +15pt:
     --header-total: 75px   (top rule + content + bottom margin)
     --logo-h:       31px   (matches reverse + colour SVGs at 220px wide) */
.brand-stripes-header {
  height: 50px;                   /* 7px rule above + 50px header + 18px gap = 75px */
  padding: 9px 0 10px;
  margin-bottom: 18px;
  display: flex;
  align-items: center;
  box-sizing: border-box;
}
.brand-stripes-header.compact { height: 38px; padding: 4px 0 4px; margin-bottom: 10px; }
.brand-stripes-logo {
  display: block;
  width: 220px;                   /* iter 101 — was 190px, +15% */
  max-width: 48%;
  height: auto;
  object-fit: contain;
  margin: 0;
}
.brand-stripes-header.compact .brand-stripes-logo { width: 175px; }

/* ── BAND style header — full-width gradient panel + white logo ───────── */
/* Locked to 60px content + 15px margin-bottom = 75px (matches stripes 7+50+18). */
.brand-band {
  background: linear-gradient(90deg, ${BRAND_NAVY} 0%, ${BRAND_TEAL} 100%);
  margin: 0 -36px 15px;
  height: 60px;
  padding: 14px 36px 15px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  box-sizing: border-box;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
.brand-band.compact { height: 45px; padding: 6px 36px; margin-bottom: 10px; }
.brand-band-logo {
  display: block;
  width: 220px;                   /* iter 101 — was 190px, +15% */
  max-width: 48%;
  height: auto;
  object-fit: contain;
}
.brand-band.compact .brand-band-logo { width: 175px; }

/* ── BLACK & WHITE style — same layout as the colour band, but a pure
   monochrome palette: black→light-grey gradient, dark-grey solid pills,
   subtle hairline borders. Internal class names is-silver / style-silver
   are kept from the earlier iteration to avoid widespread renames; the
   visual is now a true B&W finish per user request iter 97. */
.brand-band.is-silver {
  background: linear-gradient(90deg, #111827 0%, #6b7280 100%);
}
.brand-band.is-silver .brand-band-logo {
  /* Full desaturate — teal mark collapses to a matching grey */
  filter: grayscale(1) brightness(1.6);
}
.style-silver .brand-rule,
.style-silver .brand-rule-bottom,
.style-silver .contact-separator,
.style-silver .website-footer-bar {
  background: linear-gradient(90deg, #111827 0%, #6b7280 100%) !important;
}
.style-silver .contact-block { border-left-color: #6b7280 !important; }
.style-silver .contact-row strong,
.style-silver .back .back-contact strong { background: #1f2937 !important; }
.style-silver .patient-ribbon { border-color: #6b728040 !important; background: #f9fafb !important; }
.style-silver .bullet-icon { background: #111827 !important; }
.style-silver .office-hours { border-left-color: #6b7280 !important; }
.style-silver .office-hours > svg { color: #374151 !important; }
.style-silver .office-hours .oh-label { color: #374151 !important; }
.style-silver .back .back-title { border-bottom-color: #6b7280 !important; }
.style-silver .back .map-card { border-color: #6b728040 !important; }
.style-silver .back .parking-note { border-color: #6b728040 !important; background: #f9fafb !important; }
.style-silver .back .parking-note strong { background: #111827 !important; }
.style-silver .back .qr-block { border-color: #6b728040 !important; }
.style-silver .back .back-address { border-left-color: #6b7280 !important; }
/* B&W version — desaturate the OpenStreetMap tiles + recolour the map
   marker so the entire back page reads as truly monochrome. */
.style-silver .back .map-tiles img {
  filter: grayscale(1) contrast(1.05) !important;
}
.style-silver .back .map-marker .pin {
  background: #111827 !important;
}

/* ── contact block (used on the front, both styles) ───────────────────── */
.contact-block {
  margin-top: 8px;
  padding-left: 22px;
  border-left: 4px solid ${BRAND_TEAL};
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
.style-band .contact-block { margin-top: 4px; }
.addr-stack { margin-bottom: 16px; }
.addr-line {
  font-size: 18px;
  color: ${BRAND_NAVY};
  line-height: 1.55;
  margin: 0;
  font-weight: 500;
}
.contact-row {
  margin-top: 12px;
  font-size: 15px;
  color: ${BRAND_NAVY};
  line-height: 1;
}
.contact-row > span {
  display: inline-block;
  margin-right: 24px;
  vertical-align: middle;
  white-space: nowrap;
  line-height: 16px;
}
.contact-row .pill-value {
  display: inline-block;
  vertical-align: middle;
  font-size: 15px;
  color: inherit;
  line-height: 16px;
}
/* ── Unified pill style (PHONE / FAX / EMAIL / PARKING) ──────────────────
   Iter 109 — pills are now <img> tags with inline SVG content (see <Pill>
   component above). This bypasses every flex / table-cell / line-height
   alignment quirk in html2canvas — an <img> with a fixed pixel size
   cannot be misaligned. The legacy <strong> rule below is retained as a
   safety net for any spot that still uses the old markup. */
.pill-img {
  display: inline-block;
  vertical-align: middle;
  width: 78px;                /* iter 110 — bigger pill (icons + label) */
  height: 16px;
  margin-right: 8px;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
/* Parking pill is wider — icon + longer "PARKING" label */
.pill-img[data-pill="parking"] { width: 88px; }

.contact-row-svg {
  display: block;
  width: auto;
  max-width: 100%;
  height: auto;
}
.contact-row-svg[data-size="back"] { max-width: 100%; }
.parking-row-svg { display: block; width: auto; max-width: 100%; height: auto; }
.footer-bar-svg { display: block; margin: 0 auto; height: 18px; width: auto; }
/* B&W variant of pills is rendered by the Pill component itself —
   it picks the grey palette when style prop is silver, so no CSS
   filter is required here. */

.contact-row strong,
.back .back-contact strong,
.back .parking-note strong {
  display: inline-block;
  height: 13px;
  line-height: 13px;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #fff;
  background: ${BRAND_TEAL};
  padding: 0 8px;
  border-radius: 3px;
  margin-right: 6px;
  vertical-align: middle;
}
/* Thin gradient separator — sits OUTSIDE .contact-block so the vertical
   teal rail terminates above it. Symmetric horizontal margin so the line
   visually crosses the rail (extending equally left and right of it). */
.contact-separator {
  height: 1.5px;
  background: linear-gradient(90deg, ${BRAND_NAVY} 0%, ${BRAND_TEAL} 100%);
  margin: 12px -26px 0;        /* iter 103 — 10pt inset from each page edge
                                    (page padding is 36px, so -26px leaves
                                    a 10pt margin on each side) */
  border-radius: 1px;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
.blank-area { min-height: 80px; }   /* visible spacer; flex grows to fill */

/* ── Page 2 back specifics ────────────────────────────────────────────── */
/* Iter 103 — back-page elements shrunk ~35% to fit comfortably on one A4
   page with the slimmer footer and tighter print margins. */
.back .back-title {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 6px 0 12px;
  padding-bottom: 6px;
  border-bottom: 1px solid ${BRAND_TEAL}40;
}
.back .back-title h2 {
  font-family: 'Outfit', 'Helvetica Neue', Arial, sans-serif;
  font-size: 20px;
  font-weight: 600;
  color: ${BRAND_NAVY};
  margin: 0;
}
.back .back-title-icon { color: ${BRAND_NAVY}; flex-shrink: 0; width: 16px; height: 16px; }
.style-silver .back .back-title-icon { color: #111827 !important; }
.back .map-card {
  width: 100%;
  max-width: 560px;             /* iter 103 — was 768px */
  margin: 0 auto 14px;
  border: 1px solid ${BRAND_TEAL}40;
  border-radius: 6px;
  overflow: hidden;
  background: #f1f5f9;
  position: relative;
  aspect-ratio: 16 / 9;
  -webkit-print-color-adjust: exact;
  print-color-adjust: exact;
}
.back .map-tiles {
  width: 1280px;
  height: 1280px;
  display: grid;
  grid-template-columns: repeat(5, 256px);
  grid-template-rows: repeat(5, 256px);
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%) scale(0.44);   /* iter 103 — was 0.6 */
  transform-origin: 50% 50%;
  max-width: none;
}
.back .map-tiles img { display: block; width: 256px; height: 256px; }
.back .map-marker { position: absolute; width: 0; height: 0; pointer-events: none; z-index: 2; }
.back .map-marker .pin {
  width: 18px; height: 18px;
  background: #DC2626;
  border: 2px solid #fff;
  border-radius: 50% 50% 50% 0;
  transform: translate(-50%, -100%) rotate(-45deg);
  box-shadow: 0 2px 4px rgba(0,0,0,0.3);
}
.back .map-attribution {
  position: absolute; bottom: 3px; right: 6px;
  font-size: 7px; color: #475569;
  margin: 0; background: rgba(255,255,255,0.85);
  padding: 1px 3px; border-radius: 2px; z-index: 3;
}
.back .parking-note {
  margin: 0 0 14px;
  padding: 6px 10px;
  border: 1px solid ${BRAND_TEAL}40;
  border-radius: 8px;
  background: #f6fdfd;
}
.back .back-bottom { display: block; }
.back .back-address {
  padding-left: 14px;
  border-left: 3px solid ${BRAND_TEAL};
  /* Leave space on the right so address text doesn't run under the QR
     code that's anchored to the bottom-right corner of the page. */
  padding-right: 200px;
}
.back .back-address-text { margin-bottom: 12px; }
.back .back-contact {
  margin-top: 8px;
  line-height: 1;
}
.back .back-contact > span {
  display: inline-block;
  margin-right: 16px;
  vertical-align: middle;
  color: ${BRAND_NAVY};
  font-size: 13px;
  white-space: nowrap;
  line-height: 16px;
}
.back .back-contact .pill-value {
  display: inline-block;
  vertical-align: middle;
  font-size: 13px;
  line-height: 16px;
}
.back .back-address-logo {
  display: block;
  width: 160px;                            /* iter 103 — was 220 */
  max-width: 100%;
  height: auto;
  object-fit: contain;
  margin: 0 0 8px;
}
.back .back-address .addr-header {
  font-family: 'Outfit', 'Helvetica Neue', Arial, sans-serif;
  font-size: 18px;
  color: ${BRAND_NAVY};
  margin: 0 0 6px;
  font-weight: 600;
}
.back .back-address .addr-big {
  font-size: 14px;
  color: ${BRAND_NAVY};
  margin: 0 0 3px;
  font-weight: 600;
  line-height: 1.3;
}
.back .back-contact p { font-size: 12px; color: ${BRAND_NAVY}; margin: 0 0 4px; }   /* fallback for any <p> usage */
.back .qr-block {
  border: 1px solid ${BRAND_TEAL}40;
  border-radius: 8px;
  padding: 10px;
  text-align: center;
  background: #fff;
}
/* iter 106 — anchor the QR block to the bottom-right corner of the
   back page, just above the website footer bar (~25px + 10px margin).
   The .letter-page is position:relative so absolute positioning is
   measured against the page itself. */
.back .qr-corner {
  position: absolute;
  right: 36px;
  bottom: 56px;
  width: 175px;
  z-index: 2;
}
.back .qr-block { padding: 8px; border-radius: 6px; }
.back .qr-block img { width: 100%; max-width: 160px; height: auto; display: inline-block; }
.back .qr-caption {
  font-size: 8.5px;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: ${BRAND_NAVY};
  margin: 5px 0 0;
  line-height: 1.25;
}
.back .qr-link { display: none; }   /* iter 106 — long URL hidden, QR is enough */

/* ── PRINT ────────────────────────────────────────────────────────────── */
@media print {
  html, body {
    background: #fff !important;
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  * {
    -webkit-print-color-adjust: exact !important;
    print-color-adjust: exact !important;
  }
  .print-hidden, header, nav, [data-section-nav] { display: none !important; }
  .letter-page {
    border: none !important;
    box-shadow: none !important;
    border-radius: 0 !important;
    padding: 0 6mm 0 !important;       /* iter 103 — near-flush edges */
    max-width: none !important;
    /* Use 296mm (not 297mm) to leave a 1mm safety margin — Chrome's
       print renderer otherwise overflows by a hair and emits a phantom
       3rd blank sheet. */
    min-height: 296mm !important;
    height: 296mm !important;
    max-height: 296mm !important;
    overflow: hidden !important;
    page-break-after: always;
    page-break-inside: avoid;
    break-after: page;
    break-inside: avoid;
  }
  .letter-page:last-of-type {
    page-break-after: auto !important;
    break-after: auto !important;
  }
  body > div:not(.print-root),
  [data-sonner-toaster],
  [data-onboarding-banner],
  [data-chat-bubble] { display: none !important; }
  /* Bars + band extend flush to A4 edges (matches new -6mm page padding) */
  .brand-rule         { margin: 0 -6mm 0 !important; border-radius: 0 !important; }
  .brand-band         { margin: 0 -6mm 12px !important; }
  .brand-band.compact { margin-bottom: 8px !important; }
  .website-footer-bar { margin: 8px -6mm 0 !important; }
  .contact-separator  { margin: 10px -26px 0 !important; }
  .blank-area { min-height: 2mm !important; }
  @page { size: A4; margin: 0; }
}
`;
